import {
  type GenericAiWidgetData,
  getGenericAiWidgetService,
  WidgetOutputMode,
} from '@playbooks/action-cards-utils/src'
import { cn } from '@playbooks/ui/lib/utils'
import type { FunctionComponent } from 'react'
import { useRef, useState } from 'react'

import { useTranslation } from '../../contexts/localization/Localization'
import { processReplacements } from '../../utils/processReplacements'
import AiDisclaimer from './aiDisclaimer'
import MarkdownWithCopyButton from '../../components/ui/MarkdownWithCopyButton'
import { UndoIcon } from '../../components/ui'
import processPromptReplacements from '../../utils/ai/processPromptReplacements'
import { type WidgetProps } from './widgetTypes'
import { useSectionContext } from '../../contexts/SectionContext'
import { ClipboardPaste, ClipboardCheck } from 'lucide-react'
import { removeMarkdown } from '../../utils/removeFormatting'
import { usePlaybooksBackendClient } from '../../hooks/usePlaybooksBackendClient'
import { CustomButton } from '../../components/ui/CustomButton'
import { useAppContext } from '../../contexts/AppContext'

interface GenericAiWidgetProps extends WidgetProps {
  widget: GenericAiWidgetData
}

/**
 * Widget for generating text using AI model and an arbitrary pre-configured prompt.
 * @story User Story 8109: Productize AI-supported sections
 */
const GenericAiWidget: FunctionComponent<GenericAiWidgetProps> = ({ widget, context }) => {
  const { isLocked } = useSectionContext()
  const { data: state, update: updateState, questionsWithCode } = useAppContext()

  // Custom hooks
  const playbooksBackendClient = usePlaybooksBackendClient()

  const { text: t } = useTranslation()
  const [textCopied, setTextCopied] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // State
  const [isLoading, setIsLoading] = useState(false)
  const [streamedText, setStreamedText] = useState<string>()

  const outputMode = widget.outputMode || WidgetOutputMode.Add
  const shouldStreamResponse = outputMode === WidgetOutputMode.Add && widget.streamResponse === true
  const displayedText = streamedText ?? widget.value

  /**
   * Handle button click event.
   */
  async function handleButtonClick() {
    setIsLoading(true)
    setStreamedText(undefined)
    try {
      // Process replacements in the prompt
      let prompt = widget.prompt
      prompt = processReplacements(prompt, questionsWithCode) || ''
      prompt = await processPromptReplacements({
        prompt,
        actionCardsData: state,
        context,
      })
      prompt = adjustPrompt(prompt, outputMode)
      const model = widget.model

      // Send request to AI service
      let generatedText: string
      if (shouldStreamResponse) {
        let nextStreamedText = ''
        generatedText = await playbooksBackendClient.generateTextStream({ prompt, model }, chunk => {
          // Update the streamed text as chunks arrive
          nextStreamedText += chunk
          setStreamedText(nextStreamedText)
        })
      } else {
        generatedText = await playbooksBackendClient.generateText({ prompt, model })
      }

      // Update the state with the generated text
      getGenericAiWidgetService(widget).setValue(generatedText)
      // Update the Action Card state (widget value, tags)
      await updateState()

      console.log(`Generated text: ${generatedText}`)
    } finally {
      setStreamedText(undefined)
      setIsLoading(false)
    }
  }
  return (
    <div>
      <div className="grid grid-cols-8 gap-2">
        {/* "Generate" button */}
        <div className={cn(displayedText ? 'col-span-5' : 'col-span-8')}>
          <CustomButton
            size={'lg'}
            isLoading={isLoading}
            disabled={isLocked || isLoading}
            onClick={async () => {
              if (!isLoading) await handleButtonClick()
            }}
          >
            {widget.buttonLabel || t('genericAiWidget_defaultButton')}
          </CustomButton>
        </div>

        <div className="ml-auto gap-2 flex col-span-3">
          {/* "Undo" button */}
          {displayedText && (
            <>
              <CustomButton
                size={'icon-lg'}
                onClick={() => {
                  getGenericAiWidgetService(widget).setValue(undefined)
                  updateState()
                }}
                className={`${isLocked ? 'pointer-events-none cursor-not-allowed' : ''}`}
                tooltip={t('genericAiWidget_undoTooltip')}
                disabled={isLocked || isLoading}
              >
                <UndoIcon className="size-3.5" />
              </CustomButton>
              <CustomButton
                size={'icon-lg'}
                onClick={async () => {
                  const html = displayedText || ''
                  const tempDiv = document.createElement('div')
                  tempDiv.classList.add('isolated-html-content')
                  tempDiv.innerHTML = html
                  tempDiv.style.position = 'absolute'
                  tempDiv.style.left = '-9999px'
                  tempDiv.style.opacity = '0'
                  document.body.appendChild(tempDiv)
                  const plainText =
                    widget.outputMode === WidgetOutputMode.Add ? removeMarkdown(displayedText || '') : tempDiv.innerText
                  document.body.removeChild(tempDiv)

                  // Copy both HTML and plain text
                  await navigator.clipboard.write([
                    new ClipboardItem({
                      'text/html': new Blob(
                        [widget.outputMode === WidgetOutputMode.Add ? ref.current?.innerHTML || '' : html],
                        { type: 'text/html' },
                      ),
                      'text/plain': new Blob([plainText], { type: 'text/plain' }),
                    }),
                  ])

                  setTextCopied(true)
                  setTimeout(() => {
                    setTextCopied(false)
                  }, 2000)
                }}
                className={`${isLocked ? 'pointer-events-none cursor-not-allowed' : ''}`}
                tooltip={t('genericAiWidget_copyTooltip')}
                disabled={isLocked || isLoading}
              >
                {textCopied ? <ClipboardCheck /> : <ClipboardPaste />}
              </CustomButton>
            </>
          )}
        </div>
      </div>

      {/* Generated text */}

      <div ref={ref}>
        {displayedText && outputMode === WidgetOutputMode.Add && (
          <MarkdownWithCopyButton markdownText={displayedText} />
        )}
      </div>

      {/* Disclaimer message */}
      {displayedText && (
        <div className="text-right mt-3">
          <AiDisclaimer allowReporting={true} />
        </div>
      )}
    </div>
  )
}

function adjustPrompt(prompt: string, outputMode: WidgetOutputMode): string {
  if (outputMode === WidgetOutputMode.Replace) {
    // AI tends to use markdown formatting, but in "replace" mode
    // we need plain HTML as it's rendered as a rich text (#12658)
    return `${prompt}. Please format the response using simple HTML tags (ready to be pasted into a rich text editor). Do not include any introductory or concluding phrases.`
  }
  return prompt
}

export default GenericAiWidget
